"use client";

import { motion } from "framer-motion";
import { useState, useEffect } from "react";

import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";

import ChipSerif from "@/components/global/ui/ChipSerif";
import { PageAssetsSchema } from "@/sanity/pages/PageAssetsTemplate";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselApi,
} from "@/shadcn/ui/carousel";
import cardbg from "@/public/assets/cardbg.png";
import Image from "@/helpers/Image";
import { cn } from "@/lib/utils";
import { CTAButton } from "@/shadcn/ui/button";

function PerformerCard({
  item,
}: {
  item: {
    image: GROQImage;
    name: string;
    value: string;
    period: string;
    invited: string;
  };
}) {
  return (
    <div className="w-full p-[3rem] bg-[#FFF7E2] rounded-[2rem] relative overflow-clip flex flex-col">
      <Image src={cardbg} alt="" className="!w-full absolute inset-0" />
      <span className="lg:hidden relative z-[1] w-fit text-[#103037] font-medium text-[2.25rem] bg-[#103037]/5 rounded-full px-[2rem] py-[1rem] mb-[5.5rem]">
        {item.invited}
      </span>
      <div className="flex items-center gap-[1rem] relative z-[1] lg:mt-[19rem]">
        <Image src={item.image} alt="" className="!w-[4rem] rounded-full" />
        <span className="text-[#103037] font-medium text-[2.25rem]">
          {item.name}
        </span>
      </div>
      <div className="flex items-center justify-between relative z-[1] mt-[2rem]">
        <div className="flex items-end gap-[1rem] ">
          <span className="text-[#103037] font-[600] text-[4rem] leading-none">
            {item.value}
          </span>
          <span className="text-[#103037]/60 font-medium text-[2rem]">
            {item.period}
          </span>
        </div>

        <span className="hidden lg:flex text-[#103037] font-medium text-[2.25rem] bg-[#103037]/5 rounded-full px-[2rem] py-[1rem]">
          {item.invited}
        </span>
      </div>
    </div>
  );
}

export default function BestPerformers({ data }: { data: PageAssetsSchema }) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
      setCanPrev(api.canScrollPrev());
      setCanNext(api.canScrollNext());
    };

    onSelect();
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api]);
  return (
    <section className="w-full h-full lg:px-[3rem]">
      <div className="relative mx-auto w-[min(230rem,100%)] bg-[#103037] lg:rounded-[5.5rem] py-[8rem] lg:py-[15rem]">
        <div className="relative mx-auto w-full z-[1] flex flex-col items-start lg:items-center px-[3rem] lg:px-0">
          {data?.bestPerformersChip && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <ChipSerif
                text={data.bestPerformersChip}
                className="text-white mb-[1.5rem]"
              />
            </motion.div>
          )}

          <motion.div
            className="richtext richtext-start lg:richtext-center
            prose-headings:text-white [&_span]:text-white [&_span]:font-instrument-sans [&_span]:font-semibold lg:[&_span]:font-bold lg:[&_span]:text-[#6DA991] lg:[&_span]:font-gestura
            prose-p:text-white/70 prose-p:text-[2.25rem] lg:prose-p:text-[2rem] prose-p:w-[min(46.5rem,95%)] prose-strong:text-white [text-wrap-style:pretty]"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            {data?.bestPerformersText && (
              <PortableText
                value={data?.bestPerformersText}
                components={richTextComponent}
              />
            )}
          </motion.div>

          {data?.bestPerformersCTA && (
            <CTAButton className="mt-[3rem]" data={data.bestPerformersCTA} />
          )}
        </div>
        <div className="w-full mt-[4rem] lg:mt-[7rem] relative">
          <div className="hidden lg:block absolute z-[5] left-0 top-0 h-full w-[45.125rem] bg-gradient-to-r from-[#103037] to-transparent" />
          <div className="hidden lg:block absolute z-[5] right-0 top-0 h-full w-[45.125rem] bg-gradient-to-l from-[#103037] to-transparent" />
          <Carousel
            opts={{ loop: true, align: "center" }}
            setApi={setApi}
            className="w-full"
          >
            <CarouselContent>
              {/*{data.bestPerformersList.map((item, index) => (
                <CarouselItem
                  key={"bestPerformer" + index}
                  className="basis-[42.75rem] lg:basis-[55.875rem]"
                >
                  <PerformerCard item={item} />
                </CarouselItem>
              ))}*/}
              {data.bestPerformersCertificates &&
                data.bestPerformersCertificates.map((item, index) => (
                  <CarouselItem
                    key={"bestPerformer" + index}
                    className="basis-[42.75rem] lg:basis-[55.875rem]"
                  >
                    <Image src={item.image} alt="" className="!w-full" />
                  </CarouselItem>
                ))}
            </CarouselContent>
          </Carousel>

          {/* Progress Bar and Navigation - btn bar btn layout */}
          <div className="flex items-center justify-center gap-[1rem] flex-shrink-0 mt-[3rem] px-[3rem] lg:px-[7rem]">
            {/* Previous Button */}
            <button
              onClick={() => api?.scrollPrev()}
              className="w-[7rem] h-[6rem] flex-shrink-0 rounded-full flex items-center justify-center bg-white/5 transition-all text-[#103037] disabled:bg-white/10 disabled:text-white disabled:cursor-not-allowed"
              disabled={!api || !canPrev}
              aria-label="Previous slide"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                className="w-[3rem] h-auto flex-shrink-0 translate-x-[.125rem] rotate-180"
              >
                <rect width="24" height="24" fill="white" fillOpacity="0.01" />
                <path
                  d="M13.5 4.5L12.4275 5.54475L18.1125 11.25H3V12.75H18.1125L12.4275 18.4298L13.5 19.5L21 12L13.5 4.5Z"
                  fill="white"
                />
              </svg>
            </button>

            {/* Full Progress Bar */}
            <div className="h-[0.5rem] bg-white/20 rounded-full overflow-hidden w-full max-w-[34rem]">
              <div
                className="h-full bg-[#D9F9B9] transition-all duration-300 ease-out"
                style={{
                  width: `${((current + 1) / data.bestPerformersList.length) * 100}%`,
                }}
              />
            </div>

            {/* Next Button */}
            <button
              onClick={() => api?.scrollNext()}
              className="w-[7rem] h-[6rem] flex-shrink-0 rounded-full flex items-center justify-center bg-white/5 transition-all text-[#103037] disabled:bg-white/10 disabled:text-white disabled:cursor-not-allowed"
              disabled={!api || !canNext}
              aria-label="Next slide"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                className="w-[3rem] h-auto flex-shrink-0 translate-x-[.125rem]"
              >
                <rect width="24" height="24" fill="white" fillOpacity="0.01" />
                <path
                  d="M13.5 4.5L12.4275 5.54475L18.1125 11.25H3V12.75H18.1125L12.4275 18.4298L13.5 19.5L21 12L13.5 4.5Z"
                  fill="white"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
